import apiClient from "./apiClient";
import { isAdminSession } from '@/utils/adminFreshness';

// expira en 10 minutos
const CACHE_DURATION = 10 * 60 * 1000; 

const isCacheValid = (timestamp) => {
  if (isAdminSession() || !timestamp) return false;
  const now = Date.now();
  const lastUpdate = parseInt(timestamp);
  return (now - lastUpdate) < CACHE_DURATION;
};

const invalidateCache = () => {
  localStorage.removeItem('userOrders');
  localStorage.removeItem('adminOrders');
  localStorage.removeItem('ordersTimestamp');
};

export const getUserOrders = async () => {
  try {
    const cachedData = localStorage.getItem('userOrders');
    const cacheTimestamp = localStorage.getItem('ordersTimestamp');
    
    // Verificar si el caché es válido y existe
    if (cachedData && cacheTimestamp && isCacheValid(cacheTimestamp)) {
      console.log('Usando caché de órdenes, tiempo restante:', 
        Math.round((CACHE_DURATION - (Date.now() - parseInt(cacheTimestamp))) / 1000), 'segundos');
      return JSON.parse(cachedData);
    }

    console.log('Caché expirado o no existe, obteniendo datos frescos');
    
    // Si no hay caché válido, hacer petición
    const response = await apiClient.get('/orders/my-orders/', {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
      }
    });
    
    console.log('User orders response:', response.data);
    
    // Handle different response formats
    let ordersData;
    if (response.data && response.data.results) {
      // Paginated response
      ordersData = response.data.results;
    } else if (response.data && response.data.orders) {
      // Non-paginated response with custom structure
      ordersData = response.data.orders;
    } else if (Array.isArray(response.data)) {
      // Direct array response
      ordersData = response.data;
    } else {
      ordersData = [];
    }
    
    // Guardar en caché con timestamp actual
    if (!isAdminSession()) {
      localStorage.setItem('userOrders', JSON.stringify(ordersData));
      localStorage.setItem('ordersTimestamp', Date.now().toString());
    }
    
    console.log('Final orders data being returned:', ordersData);
    return ordersData;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al obtener las órdenes');
  }
};

export const getOrders = async (ignoreCache = false) => {
  try {
    const cachedData = localStorage.getItem('adminOrders');
    const cacheTimestamp = localStorage.getItem('ordersTimestamp');
    
    // Si ignoreCache es true o el caché no es válido, hacer petición nueva
    if (!ignoreCache && cachedData && cacheTimestamp && isCacheValid(cacheTimestamp)) {
      return JSON.parse(cachedData);
    }

    const response = await apiClient.get("/orders/", {
      params: { page_size: 200 },
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
      }
    });
    
    console.log('Admin orders response:', response.data);
    
    // Handle paginated response - extract results array
    let ordersData;
    if (response.data && response.data.results) {
      ordersData = response.data.results;
    } else if (Array.isArray(response.data)) {
      ordersData = response.data;
    } else {
      ordersData = [];
    }
    
    if (!isAdminSession()) {
      localStorage.setItem('adminOrders', JSON.stringify(ordersData));
      localStorage.setItem('ordersTimestamp', Date.now().toString());
    }
    
    return ordersData;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al obtener las órdenes');
  }
};

export const createOrder = async (orderData) => {
  try {
    const token = localStorage.getItem('accessToken');
    // Ensure the payload matches the expected format
    const payload = {
      recipient_name: orderData.recipient_name, // Make sure this is included
      shipping_address: orderData.shipping_address,
      customer_email: orderData.customer_email,
      customer_phone: orderData.customer_phone,
      delivery_type: orderData.delivery_type,
      products: orderData.products.map(item => ({
        product_id: item.product_id,
        blister_id: item.blister_id,
        quantity: item.quantity,
        ...(item.variant_display ? { variant_display: item.variant_display } : {}),
      }))
    };

    if (orderData.coupon_code) {
      payload.coupon_code = orderData.coupon_code
    }
    if (orderData.payment_method) {
      payload.payment_method = orderData.payment_method
    }

    const response = await apiClient.post('/orders/', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    invalidateCache();
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al crear la orden');
  }
};

export const contactOrderAdmin = async (orderId, message) => {
  const { data } = await apiClient.post(`/orders/${orderId}/contact-admin/`, { message })
  return data
}

export const getOrderDetails = async (orderId) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.get(`/orders/${orderId}/`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al obtener los detalles de la orden');
  }
};

export const getOrder = async (id) => {
  apiClient.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem('accessToken')}`;
  const response = await apiClient.get(`/orders/${id}/`);
  return response.data;
};

export const updateOrder = async (orderId, orderData) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.put(`/orders/${orderId}/`, orderData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    // Invalidar todos los cachés al actualizar una orden
    invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error en updateOrder:', error.response?.data);
    throw new Error(error.response?.data?.message || 'Error al actualizar el pedido');
  }
};

export const cancelOrder = async (orderId) => {
  try {
    const response = await apiClient.post(`/orders/${orderId}/cancel/`);
    invalidateCache();
    return response.data;
  } catch (error) {
    const msg =
      error.response?.data?.error ||
      error.response?.data?.message ||
      'Error al cancelar el pedido';
    throw new Error(msg);
  }
};

export const deleteOrder = async (id) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.delete(`/orders/${id}/`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    // Invalidate cache after deletion
    invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error deleting order:', error.response?.data);
    throw new Error(error.response?.data?.message || 'Error al eliminar el pedido');
  }
};

export const updateOrderStatus = async (orderId, status) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.patch(
      `/orders/${orderId}/update_status/`,
      { status },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    invalidateCache();
    return response.data;
  } catch (error) {
    const msg =
      error.response?.data?.error ||
      error.response?.data?.message ||
      'Error al actualizar el estado';
    throw new Error(msg);
  }
};

export const notifyOrderShipping = async (orderId) => {
  try {
    const response = await apiClient.post(`/orders/${orderId}/notify_shipping/`);
    invalidateCache();
    return response.data;
  } catch (error) {
    const msg =
      error.response?.data?.error ||
      error.response?.data?.message ||
      'Error al enviar la notificación de envío';
    throw new Error(msg);
  }
};

export const toggleArchiveOrder = async (orderId, forceArchive = null) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.post(`/orders/${orderId}/toggle-archive/`, 
      { force_archive: forceArchive },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    
    // Invalidate cache to reflect changes
    invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error toggling archive status:', error.response?.data);
    throw new Error(error.response?.data?.message || 'Error al cambiar el estado de archivo del pedido');
  }
};
